import React, { useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronDown,
  Search,
  Eye,
  Users,
  X,
  CheckCircle2,
  Clock,
  FileQuestion,
} from "lucide-react";
import { reportData } from "../../../../Redux/Teacher_Panel/report/report.action";
import "./reportDetailedView.scss";
import { analyticsReport } from "../../../../Redux/analytics/analytics.action";
import { formatDateTime } from "../../../../utils/utils";
import { pal, hex2rgba, gradeBadge } from "../../../../Reusable_Components/Report";

function getClassName(a) {
  return a.assigned_class || a.class_name || a.class || a.standard || "—";
}
function getDivision(a) {
  return a.assigned_division || a.division || a.section || "—";
}

export default function ReportDetailedView() {
  const dispatch = useDispatch();
  const nav = useNavigate();

  const reportDataStore = useSelector(
    (s) => s?.reportManager?.reportDataArray?.assignments,
  );

  useEffect(() => {
    dispatch(reportData());
  }, [dispatch]);

  // filters
  const [classFilter, setClassFilter] = useState("All");
  const [divisionFilter, setDivisionFilter] = useState("All");
  const [subjectFilter, setSubjectFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All"); // All | Attempted | Pending
  const [search, setSearch] = useState("");

  const [dateFrom, setDateFrom] = useState("");   // "YYYY-MM-DD"
  const [dateTo, setDateTo] = useState("");       // "YYYY-MM-DD"

  // row expansion (student roster for a test)
  const [expandedId, setExpandedId] = useState(null);

  // question-paper preview drawer
  const [selectedTest, setSelectedTest] = useState(null);
  const [detailQuestions, setDetailQuestions] = useState([]);

  /* Flattens assignment_data.{mcq, short_answer, fill_in_the_blank}
   into one array the preview drawer already knows how to render. */
  function normalizeQuestions(a) {
    const ad = a.assignment_data || {};

    const mcq = (ad.mcq || []).map((q) => ({
      id: q.question_id,
      type: "MCQ",
      text: q.question,
      options: q.options || [],
      correct: q.correct_answer,
      answer: q.correct_answer,
      marks: q.marks ?? 1,
      topic: q.topic,
      explanation: q.explanation,
    }));

    const short = (ad.short_answer || []).map((q) => ({
      id: q.question_id,
      type: "SHORT",
      text: q.question,
      answer: q.correct_answer,
      marks: q.marks ?? 2,
      topic: q.topic,
      explanation: q.explanation,
    }));

    const fib = (ad.fill_in_the_blank || []).map((q) => ({
      id: q.question_id,
      type: "FIB",
      text: q.question,
      answer: q.correct_answer,
      marks: q.marks ?? 1,
      topic: q.topic,
      explanation: q.explanation,
    }));

    return [...mcq, ...short, ...fib];
  }

  function normalizeStudentAnswers(s) {
    const raw = s.student_assignment_data;
    if (!raw || !raw.length) return null;

    const typeMap = {
      mcq: "MCQ",
      short_answer: "SHORT",
      fill_in_the_blank: "FIB",
    };

    return raw.map((q, i) => ({
      id: q.question_id ?? i,
      type:
        typeMap[q.question_type] || q.question_type?.toUpperCase() || "SHORT",
      text: q.question,
      options: q.options || [],
      correct: q.correct_answer,
      answer: q.correct_answer,
      userAnswer: q.user_answer,
      evaluation: q.evaluation, // "correct" | "incorrect"
      marksAwarded: q.marks_awarded,
      marks: q.max_marks ?? 1,
      topic: q.topic,
      explanation: q.explanation,
    }));
  }

  /* ── Build one row per test/assignment ── */
  const testList = useMemo(() => {
    if (!reportDataStore?.length) return [];
    return reportDataStore.map((a, idx) => {
      const students = (a.student_details || []).map((s, i) => ({
        name: s.student_name || s.name || `Student ${i + 1}`,
        studentID: s.student_id || s.student_id || "—",
        marks: s.marks_obtained ?? null,
        maxMarks: s.max_marks ?? a.max_marks ?? null,
        percentage:
          s.percentage ??
          (s.max_marks
            ? Math.round((s.marks_obtained / s.max_marks) * 100)
            : 0),
        status: s.attempted,
        answers: normalizeStudentAnswers(s),
      }));
      const attemptedStudents = students.filter(
        (s) => s.attempted === "Attempted",
      );
      const avgScore = attemptedStudents.length
        ? Math.round(
            attemptedStudents.reduce((sum, s) => sum + (s.percentage || 0), 0) /
              attemptedStudents.length,
          )
        : 0;
      const assigned = a.no_of_students_assigned ?? students.length;
      const attempted = a.no_of_students_attempted ?? attemptedStudents.length;
      const { date, time, raw: assignedDateObj } = formatDateTime(
        a.assigned_date || a.created_date,
      );
      return {
        id: a.id ?? a._id ?? idx,
        assignment_id: a.assignment_id || "Unknown",
        subject: a.subject || "Unknown",
        chapter: a.chapter || a.topic || "Unknown",
        topic: a.topic || a.chapter || "Unknown",
        className: getClassName(a),
        division: getDivision(a),
        date,               // formatted display date
        time,               // formatted display time
        assignedDateObj,    // real Date for filtering/sorting
        assigned,
        attempted,
        attendancePct: assigned ? Math.round((attempted / assigned) * 100) : 0,
        avgScore,
        students,
        questions: normalizeQuestions(a),
        totalNoOfQuestions: a.total_questions ?? normalizeQuestions(a).length,
      };
    });
  }, [reportDataStore]);

  const classOptions = useMemo(
    () => [
      "All",
      ...Array.from(new Set(testList.map((t) => t.className))).sort(),
    ],
    [testList],
  );
  const divisionOptions = useMemo(
    () => [
      "All",
      ...Array.from(new Set(testList.map((t) => t.division))).sort(),
    ],
    [testList],
  );
  const subjectOptions = useMemo(
    () => [
      "All",
      ...Array.from(new Set(testList.map((t) => t.subject))).sort(),
    ],
    [testList],
  );

  const filteredTests = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fromTime = dateFrom ? new Date(dateFrom + "T00:00:00").getTime() : null;
    const toTime = dateTo ? new Date(dateTo + "T23:59:59").getTime() : null;

    return testList.filter((t) => {
      if (classFilter !== "All" && t.className !== classFilter) return false;
      if (divisionFilter !== "All" && t.division !== divisionFilter) return false;
      if (subjectFilter !== "All" && t.subject !== subjectFilter) return false;

      if (t.assignedDateObj) {
        const ts = t.assignedDateObj.getTime();
        if (fromTime !== null && ts < fromTime) return false;
        if (toTime !== null && ts > toTime) return false;
      } else if (fromTime !== null || toTime !== null) {
        // no date on record — exclude when a range filter is active
        return false;
      }

      if (!q) return true;
      const haystack =
        `${t.subject} ${t.chapter} ${t.className} ${t.division}`.toLowerCase();
      const studentMatch = t.students.some((s) => s.name.toLowerCase().includes(q));
      return haystack.includes(q) || studentMatch;
    });
  }, [testList, classFilter, divisionFilter, subjectFilter, statusFilter, search, dateFrom, dateTo]);

  const totals = useMemo(() => {
    const assigned = filteredTests.reduce((s, t) => s + t.assigned, 0);
    const attempted = filteredTests.reduce((s, t) => s + t.attempted, 0);
    const avg = filteredTests.length
      ? Math.round(
          filteredTests.reduce((s, t) => s + t.avgScore, 0) /
            filteredTests.length,
        )
      : 0;
    return {
      tests: filteredTests.length,
      assigned,
      attempted,
      attendancePct: assigned ? Math.round((attempted / assigned) * 100) : 0,
      avgScore: avg,
    };
  }, [filteredTests]);

  function openPreview(test, student) {
    setSelectedTest({ ...test, _studentName: student?.name });
    setDetailQuestions(
      student?.answers || student?.questions || test.questions || [],
    );
  }

  return (
    <div className="dv-wrapper">
      {/* Header */}
      <motion.div
        className="dv-header"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <button className="dv-back-btn" onClick={() => nav(-1)}>
          <ChevronLeft size={18} /> Back
        </button>
        <div>
          <h1 className="dv-title">Detailed Test Report</h1>
          <p className="dv-subtitle">
            Every class, every test, every student who attempted it
          </p>
        </div>
      </motion.div>

      {/* Summary chips */}
      <motion.div
        className="dv-summary-row"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.08 }}
      >
        <SummaryChip
          icon={<FileQuestion size={16} />}
          label="Tests"
          value={totals.tests}
          color="#6366f1"
        />
        <SummaryChip
          icon={<Users size={16} />}
          label="Assigned"
          value={totals.assigned}
          color="#0891b2"
        />
        <SummaryChip
          icon={<CheckCircle2 size={16} />}
          label="Attempted"
          value={totals.attempted}
          color="#059669"
        />
        <SummaryChip
          icon={<Clock size={16} />}
          label="Attendance"
          value={`${totals.attendancePct}%`}
          color="#d97706"
        />
      </motion.div>

      {/* Filters */}
      <motion.div
        className="dv-filters"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.12 }}
      >
        <FilterSelect
          label="Class"
          value={classFilter}
          options={classOptions}
          onChange={setClassFilter}
        />
        <FilterSelect
          label="Division"
          value={divisionFilter}
          options={divisionOptions}
          onChange={setDivisionFilter}
        />
        <FilterSelect
          label="Subject"
          value={subjectFilter}
          options={subjectOptions}
          onChange={setSubjectFilter}
        />
        <div className="dv-search-box">
          <Search size={15} className="dv-search-icon" />
          <input
            placeholder="Search subject, chapter or student name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="dv-search-input"
          />
        </div>
      </motion.div>

      {/* Test table */}
      <motion.div
        className="dv-table-card"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.16 }}
      >
        <div className="dv-table-head dv-row-grid">
          <span>Assignment ID</span>
          <span>Subject</span>
          <span>Date</span>
          <span>Class</span>
          <span>Division</span>
          <span>Attempted/Assigned</span>
          <span>Raw Paper</span>
          <span></span>
        </div>

        {filteredTests.length === 0 ? (
          <div className="dv-empty">
            <FileQuestion size={28} />
            <span>No tests match these filters</span>
          </div>
        ) : (
          filteredTests.map((t) => {
            const p = pal(t.subject);
            const isOpen = expandedId === t.id;
            return (
              <div key={t.id} className="dv-test-row-wrap">
                <div
                  className={`dv-test-row dv-row-grid${isOpen ? " dv-test-row--open" : ""}`}
                  onClick={() => setExpandedId(isOpen ? null : t.id)}
                >
                  <span>{t.assignment_id}</span>
                  <span className="dv-subject-cell">
                    <span
                      className="dv-subject-dot"
                      style={{ background: p.solid }}
                    />
                    <span>
                      <div className="dv-subject-name">{t.subject}</div>
                      <div className="dv-chapter-name">{t.chapter}</div>
                    </span>
                  </span>
                  <span>
                    <div>{t.date}</div>
                    <div className="small fw-semibold text-muted">{t.time}</div>
                  </span>
                  <span>{t.className}</span>
                  <span>{t.division}</span>
                  <span>
                    <span
                      className="dv-attempt-pill"
                      style={{
                        background: hex2rgba(p.solid, 0.12),
                        color: p.text,
                      }}
                    >
                      {t.attempted}/{t.assigned}
                    </span>
                  </span>
                  <span>
                    <button
                      className="dv-view-paper-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openPreview(t);
                      }}
                    >
                      <Eye size={14} /> View Paper
                    </button>
                  </span>
                  <span className="dv-row-actions">
                    <ChevronDown
                      size={16}
                      className={`dv-chevron${isOpen ? " dv-chevron--open" : ""}`}
                    />
                  </span>
                </div>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      className="dv-student-panel"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                    >
                      {t.students.length === 0 ? (
                        <div className="dv-empty dv-empty--small">
                          No students assigned yet
                        </div>
                      ) : (
                        <StudentRosterPanel test={t} onPreview={openPreview} />
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })
        )}
      </motion.div>

      {/* Question-paper preview drawer */}
      <AnimatePresence>
        {selectedTest && (
          <>
            <motion.div
              className="dv-drawer-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedTest(null)}
            />
            <motion.div
              className="dv-preview-drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <div className="dv-preview-drawer-header">
                <div>
                  <h4>Preview: {selectedTest.topic || selectedTest.chapter}</h4>
                  <span className="dv-preview-drawer-sub">
                    {selectedTest.subject} · Total questions{" "}
                    {selectedTest.totalNoOfQuestions}
                    {selectedTest._studentName && (
                      <>
                        {" "}
                        · <strong>{selectedTest._studentName}</strong>
                      </>
                    )}
                  </span>
                </div>
                <button
                  className="dv-close-btn"
                  onClick={() => setSelectedTest(null)}
                >
                  <X size={20} />
                </button>
              </div>
              <div className="dv-preview-drawer-body">
                {detailQuestions.length === 0 ? (
                  <p className="dv-no-q-msg">
                    No questions found for this test yet.
                  </p>
                ) : (
                  detailQuestions.map((q, i) => {
                    const hasStudentAnswer = q.userAnswer !== undefined;
                    const isCorrect = q.evaluation === "correct";
                    return (
                      <div key={i} className="dv-q-card">
                        <div className="dv-q-card-top">
                          <span className="dv-q-type-badge">{q.type}</span>
                          {hasStudentAnswer && (
                            <span
                              className="dv-q-eval-badge"
                              style={{
                                color: isCorrect ? "#059669" : "#ef4444",
                                background: isCorrect
                                  ? "rgba(5,150,105,0.1)"
                                  : "rgba(239,68,68,0.1)",
                                borderRadius: 6,
                                padding: "2px 8px",
                                fontSize: 11,
                                fontWeight: 700,
                                marginLeft: 8,
                              }}
                            >
                              {isCorrect ? "✓ Correct" : "✗ Incorrect"}
                              {q.marksAwarded != null &&
                                ` · ${q.marksAwarded}/${q.marks} Mark${q.marks !== 1 ? "s" : ""}`}
                            </span>
                          )}
                          {!hasStudentAnswer && (
                            <span className="dv-q-mark">
                              {q.marks ?? 1} Mark
                              {(q.marks ?? 1) !== 1 ? "s" : ""}
                            </span>
                          )}
                        </div>

                        <p className="dv-q-text">
                          {i + 1}. {q.text}
                        </p>

                        {q.type === "MCQ" && (
                          <div className="dv-options-grid">
                            {q.options.map((opt, idx) => {
                              const isCorrectOpt = opt === q.correct;
                              const isStudentPick =
                                hasStudentAnswer && opt === q.userAnswer;
                              return (
                                <div
                                  key={idx}
                                  className={`dv-option-item${isCorrectOpt ? " dv-option-item--correct" : ""}`}
                                  style={
                                    isStudentPick && !isCorrectOpt
                                      ? {
                                          outline: "2px solid #ef4444",
                                          background: "rgba(239,68,68,0.06)",
                                        }
                                      : undefined
                                  }
                                >
                                  {opt} {isCorrectOpt && "✓"}{" "}
                                  {isStudentPick &&
                                    !isCorrectOpt &&
                                    "✗ (selected)"}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {(q.type === "FIB" || q.type === "SHORT") && (
                          <>
                            {hasStudentAnswer && (
                              <div
                                className="dv-answer-box"
                                style={{
                                  marginBottom: 6,
                                  borderColor: isCorrect
                                    ? "#059669"
                                    : "#ef4444",
                                }}
                              >
                                <small>Student's Answer:</small>
                                <span>
                                  {q.userAnswer || (
                                    <em style={{ opacity: 0.6 }}>
                                      No answer given
                                    </em>
                                  )}
                                </span>
                              </div>
                            )}
                            <div className="dv-answer-box">
                              <small>Correct Answer:</small>
                              <span>{q.answer}</span>
                            </div>
                          </>
                        )}

                        {q.explanation && (
                          <div className="dv-explanation">
                            <strong>Explanation:</strong> {q.explanation}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

function StudentRosterPanel({ test, onPreview }) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return test.students;
    return test.students.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        String(s.studentID).toLowerCase().includes(q),
    );
  }, [test.students, search]);

  return (
    <div className="dv-student-table">
      <div className="dv-student-row dv-student-row--head">
        <span>Sr.No</span>
        <span>Student ID</span>
        <span>Student</span>
        <span>Marks </span>
        <span>Status</span>
        <span>Test Paper</span>
        <span></span>
        <span>
          <div className="dv-roster-search">
            <Search size={13} className="dv-roster-search-icon" />
            <input
              placeholder="Search student or roll no…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="dv-empty dv-empty--small">
          No students match “{search}”
        </div>
      ) : (
        filtered.map((s, i) => {
          const badge = gradeBadge(s.percentage || 0);
          return (
            <div key={i} className="dv-student-row">
              <span className="dv-muted">{i + 1}</span>
              <span className="dv-student-name">{s.studentID}</span>
              <span className="dv-student-name">{s.name}</span>
              <span>
                {s?.status === true ? (
                  <>
                    {s.marks ?? "—"}/{s.maxMarks ?? "—"}
                  </>
                ) : (
                  "—"
                )}
              </span>
              <span>
                <span
                  className={`dv-status-pill dv-status-pill--${s.status === true ? "done" : "pending"}`}
                  disabled={s.status !== true}
                >
                  {s.status === true ? "Attempted" : "Not Attempted"}
                </span>
              </span>
              <span>
                {s.status === true ? (
                  <button
                    className="dv-view-paper-btn dv-view-paper-btn--sm"
                    onClick={() => onPreview(test, s)}
                  >
                    <Eye size={13} /> View Paper
                  </button>
                ) : (
                  <button className="dv-view-paper-btn dv-view-paper-btn--sm">
                    N/A
                  </button>
                )}
              </span>
              <span></span>
              <span></span>
            </div>
          );
        })
      )}
    </div>
  );
}

function SummaryChip({ icon, label, value, color }) {
  return (
    <div className="dv-summary-chip">
      <span
        className="dv-summary-chip-icon"
        style={{ color, background: hex2rgba(color, 0.12) }}
      >
        {icon}
      </span>
      <div>
        <div className="dv-summary-chip-label">{label}</div>
        <div className="dv-summary-chip-value">{value}</div>
      </div>
    </div>
  );
}

function FilterSelect({ label, value, options, onChange }) {
  return (
    <label className="dv-filter-select">
      <span className="dv-filter-label">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}